import type { BottomLogosProps } from '@/components/renters/BottomLogos';
import type { ClaimsProps } from '@/components/renters/Claims';
import type { FaqProps } from '@/components/renters/Faq';
import type { GetCoveredProps } from '@/components/renters/GetCovered';
import type { HeroProps } from '@/components/renters/Hero';
import type { ProtectedProps } from '@/components/renters/Protected';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/renters/og.png';
import bicyclesImg from '@/static/assets/renters/stuff/lg/bicycles.svg';
import cameraImg from '@/static/assets/renters/stuff/lg/camera.svg';
import type { BasePageConfig } from '../../BasePageConfig';
import { BottomCTAProps } from '@/components/renters/BottomCTA';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Insurance policies for Renters',
  description: 'Insurance policies for Renters',
  image: [bicyclesImg.src, cameraImg.src],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '5',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does Lemonade Renters insurance cover?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Your policy covers your personal property against theft and damage, plus legal claims for damage or injury you accidentally cause others. Also, if your home becomes unlivable due to certain types of damage we’ll help cover the costs of temporary living arrangements. <a href="https://www.lemonade.com/renters/explained/what-is-renters-insurance-and-whats-covered/">Click here to learn more about what does renters insurance cover</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does renters insurance cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Lemonade renters insurance policy starts at $5 per month, but your <a href="https://www.lemonade.com/renters/explained/this-is-how-much-renters-insurance-actually-costs/">policy price</a> (aka the ‘premium’) will be affected by the coverage amounts you choose, among other factors. You can <a href="https://www.lemonade.com/start/1?f=1&ownership=renter" rel="noopener noreferrer" target="_blank">get your online renters insurance quote here.</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'What bundles and discounts does Lemonade Renters offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In addition to renters insurance, Lemonade offers homeowners, car, pet, and term life policies. You can unlock an automatic bundle discount with any combination, <a href="https://www.lemonade.com/renters/explained/renters-and-car-insurance-bundle/" target=“_blank”>like car and renters</a>, so it’s both a great way to consolidate your insurance needs and an opportunity to save. You could also score a discount on your Renters policy if your place includes protective devices, like deadbolts, smoke alarms, and burglar alarms',
      },
    },
    {
      '@type': 'Question',
      name: 'What is renters insurance?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Renters insurance is a safety net for tenants, providing help if unfortunate incidents affect you or your belongings. Your policy can step in when there\'s loss or damage due to specified "perils," including theft, fire, vandalism, certain types of water damage, and more, as outlined in your policy. For more details, check out our article on <a href="https://www.lemonade.com/renters/explained/what-is-renters-insurance/" target="_blank">"What is renters insurance?"</a>.',
      },
    },
  ],
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  coverage: Pick<GetCoveredProps, 'title' | 'subtitle'>;
  protected: ProtectedProps;
  claims: ClaimsProps;
  faq: FaqProps;
  products: ProductsProps;
  bottomLogos: BottomLogosProps;
  bottomCTA: BottomCTAProps;
}

export const config: Config = {
  layout: { ...sharedConfig['en-US'].layout },
  seo: {
    title: 'Renters Insurance - Fast, Free Quotes Online | Lemonade',
    metaTitle: 'Renters Insurance - Fast, Free Quotes Online | Lemonade',
    description:
      'Renters insurance by Lemonade offers peace of mind at an affordable price. Get your free quote for protection today, starting at $5/month.',
    ogImage: ogImage.src,
    jsonld: [productJsonld, faqSchema],
  },
  hero: {
    title: 'Renters insurance that\nkeeps your stuff safe',
    subtitle: 'Renter tested. Landlord approved.\nStarting from $5/mo.',
    ctaLabel: 'Check our prices',
    trackingLabel: 'renters_hero',
  },
  customersReviews: {
    title: 'Most loved renters insurance\nin America',
    subtitle:
      'Lemonade has earned 4.9 stars in the App store, and\nis also top-rated by Supermoney, Clearsurance, and others',
    darkBackground: true,
  },
  coverage: {
    title: 'Covered in as little as 90 seconds',
    subtitle: 'Our AI can activate your policy and protect your stuff instantly.',
  },
  protected: {
    title: 'What does renters insurance actually cover?',
    subtitle:
      'It protects your personal property and helps cover personal liability costs. Lemonade Renters has your back when bad luck strikes.',
  },
  claims: {
    title: 'Claims paid at record speed',
    subtitle: 'No paperwork. No hassle. No waiting.',
  },
  faq: {
    title: 'Good to know...',
    subtitle:
      'Renters insurance can be confusing, so we worked hard to make it simple. If\nyou still have questions, you can check out <a href="https://www.lemonade.com/renters/explained/" target="_blank" rel="noreferrer">renters insurance explained</a>',
  },
  bottomCTA: {},
  bottomLogos: {
    title: 'Protect your things,\nplus support what matters to you.',
    subtitle:
      'Lemonade is a public benefit corporation. We take a flat fee up front before we pay claims.\nThen, we donate what’s left to meaningful causes our customers choose.',
  },
  products: {
    title: 'More products from Lemonade',
    subtitle: 'Amazing savings when you bundle',
    products: [
      {
        title: 'Homeowners',
        subtitle: 'Protection for your\nhome and stuff',
        price: 'From $25/mo',
        product: 'home_us_owners',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Car',
        subtitle: 'Protect your car, passengers,\nand the planet',
        price: 'From $30/mo',
        product: 'car',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Pet',
        subtitle: 'Health insurance for furry\nfamily members',
        price: 'From $10/mo',
        product: 'pet',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Term Life',
        subtitle: 'Protecting the people\nyou love',
        price: 'From $8/mo',
        product: 'life',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
    ],
  },
};
